import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import { Box, ButtonBase, FormControl, FormHelperText, IconButton, Slider, Typography } from '@mui/material';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Controller, useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useSiloRealFormContext } from '../base/use-form-context';
import { useFormFieldStyles } from './use-form-field-styles';
import * as HOOKS from '@siloreal:shared:hooks';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type RangeFieldSx = {
  flex: number;
  minWidth: string;
};

export type RangeInputValue = {
  min: number;
  max: number;
};

type RangeFieldValue = Partial<RangeInputValue> | null;

type Props = {
  tid: string;
  name: string;
  required?: boolean;
  label?: string;
  size?: 'medium' | 'large';
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (value: number) => string;
  onChange?: (value: RangeInputValue) => void;
};

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_STEP = 1;
const GREEN = '#6BAA00';
const GREEN_SOFT = '#EFF8E7';
const GREEN_BORDER = '#CFE4B6';
const BLUE = '#22A7F0';
const BLUE_DARK = '#1C93D3';
const BLUE_SOFT = '#22A7F02A';
const BLUE_HOVER = '#22A7F042';
const TEXT_COLOR = '#2F4319';
const MUTED_TEXT = '#757575';
const OUTSIDE_RANGE_LINE = '#EFEFEF';
const UNSELECTED_ENDPOINT_LINE = '#CFDCCA';
const EMPTY_BORDER = '#BDBDBD';
const EMPTY_CORE = '#D8D8D8';
const DISABLED_TEXT = '#A8A8A8';
const DISABLED_BORDER = '#DADADA';
const DISABLED_BACKGROUND = '#F4F4F4';

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const resolveBounds = (min?: number, max?: number): RangeInputValue => {
  const resolvedMin = isFiniteNumber(min) ? min : DEFAULT_MIN;
  const resolvedMax = isFiniteNumber(max) ? max : DEFAULT_MAX;

  if (resolvedMax <= resolvedMin) return { min: DEFAULT_MIN, max: DEFAULT_MAX };

  return { min: resolvedMin, max: resolvedMax };
};

const normalizeRangeValue = (value: unknown, bounds: RangeInputValue): RangeInputValue => {
  const rawValue = Array.isArray(value)
    ? { min: value[0], max: value[1] }
    : value && typeof value === 'object'
      ? (value as Partial<RangeInputValue>)
      : {};

  const rawMin = isFiniteNumber(rawValue.min) ? rawValue.min : bounds.min;
  const rawMax = isFiniteNumber(rawValue.max) ? rawValue.max : bounds.max;
  const clampedMin = clamp(rawMin, bounds.min, bounds.max);
  const clampedMax = clamp(rawMax, bounds.min, bounds.max);

  return {
    min: Math.min(clampedMin, clampedMax),
    max: Math.max(clampedMin, clampedMax),
  };
};

const toRangeFieldValue = (value: RangeInputValue, bounds: RangeInputValue): RangeFieldValue => {
  const minSelected = value.min !== bounds.min;
  const maxSelected = value.max !== bounds.max;

  if (!minSelected && !maxSelected) return null;

  return {
    ...(minSelected ? { min: value.min } : {}),
    ...(maxSelected ? { max: value.max } : {}),
  };
};

const defaultFormatValue = (value: number): string => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '');
};

const getPositionPercent = (value: number, bounds: RangeInputValue): number => {
  return ((value - bounds.min) / (bounds.max - bounds.min)) * 100;
};

const getRangeCenterPercent = (value: RangeInputValue, bounds: RangeInputValue): number => {
  return getPositionPercent((value.min + value.max) / 2, bounds);
};

const getSummary = (
  value: RangeInputValue,
  bounds: RangeInputValue,
  formatValue: (value: number) => string
): string => {
  const lowerAtMin = value.min === bounds.min;
  const upperAtMax = value.max === bounds.max;

  if (!lowerAtMin && !upperAtMax) return `${formatValue(value.min)} — ${formatValue(value.max)}`;
  if (!lowerAtMin) return `Desde ${formatValue(value.min)}`;
  if (!upperAtMax) return `Hasta ${formatValue(value.max)}`;
  return '—';
};

type TrackBackgroundParams = {
  disabled: boolean;
  lowerAtMin: boolean;
  upperAtMax: boolean;
};

const getTrackBackground = ({ disabled, lowerAtMin, upperAtMax }: TrackBackgroundParams): string => {
  if (disabled) return '#D8D8D8';
  if (lowerAtMin && upperAtMax) return UNSELECTED_ENDPOINT_LINE;
  if (!lowerAtMin && !upperAtMax) {
    return `linear-gradient(90deg, ${GREEN} 0%, #42D296 38%, ${BLUE} 50%, #42D296 62%, ${GREEN} 100%)`;
  }
  if (!lowerAtMin) {
    return `linear-gradient(90deg, ${GREEN} 0%, rgba(107, 170, 0, 0.42) 48%, ${UNSELECTED_ENDPOINT_LINE} 100%)`;
  }

  return `linear-gradient(90deg, ${UNSELECTED_ENDPOINT_LINE} 0%, rgba(107, 170, 0, 0.42) 52%, ${GREEN} 100%)`;
};

type ValueBubbleProps = {
  show: boolean;
  label: string;
  left: number;
};

const ValueBubble = ({ show, label, left }: ValueBubbleProps) => {
  if (!show) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: `${left}%`,
        zIndex: 2,
        px: 0.75,
        py: 0.25,
        border: '1px solid',
        borderColor: GREEN_BORDER,
        borderRadius: '6px',
        backgroundColor: GREEN_SOFT,
        color: TEXT_COLOR,
        boxShadow: '0 4px 10px rgba(107, 170, 0, 0.12)',
        transform: 'translate(-50%, -85%)',
        pointerEvents: 'none',
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: 'block', fontSize: 11, fontWeight: 700, lineHeight: 1.15, whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </Box>
  );
};

type RangeSide = 'start' | 'end';

type EndpointHintProps = {
  side: RangeSide;
  show: boolean;
};

const EndpointHint = ({ side, show }: EndpointHintProps) => {
  const i18n = HOOKS.useLocale();

  if (!show) return null;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: -14,
        left: side === 'start' ? 4 : 'auto',
        right: side === 'end' ? 4 : 'auto',
        zIndex: 3,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color: '#8A8A8A',
        pointerEvents: 'none',
        transform: side === 'start' ? 'rotate(4deg)' : 'rotate(-4deg)',
        transformOrigin: side === 'start' ? 'left bottom' : 'right bottom',
      }}
    >
      {side === 'start' && <RedoRoundedIcon sx={{ fontSize: 15, transform: 'scaleX(-1) rotate(14deg)' }} />}

      <Typography
        variant="caption"
        sx={{
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        {i18n.t('temp.common.slide_exclamation')}
      </Typography>

      {side === 'end' && <RedoRoundedIcon sx={{ fontSize: 15, transform: 'scaleY(1) rotate(14deg)' }} />}
    </Box>
  );
};

type RangeCenterMarkerProps = {
  tid: string;
  left: number;
  active: boolean;
  disabled: boolean;
};

const RangeCenterMarker = ({ tid, left, active, disabled }: RangeCenterMarkerProps) => {
  const borderColor = disabled ? DISABLED_BORDER : active ? BLUE : EMPTY_BORDER;
  const backgroundColor = disabled ? DISABLED_BACKGROUND : active ? BLUE_SOFT : '#F6F6F6';
  const color = disabled ? DISABLED_TEXT : active ? BLUE : MUTED_TEXT;
  const lineBackground = disabled
    ? '#D8D8D8'
    : active
      ? `linear-gradient(180deg, ${BLUE} 0%, rgba(34, 167, 240, 0.18) 100%)`
      : `linear-gradient(180deg, ${EMPTY_BORDER} 0%, rgba(189, 189, 189, 0.18) 100%)`;

  return (
    <Box
      aria-hidden="true"
      data-testid={`${tid}-range-center-marker`}
      sx={{
        position: 'absolute',
        top: -31,
        left: `${left}%`,
        zIndex: 1,
        width: 1,
        height: 56,
        display: 'flex',
        justifyContent: 'flex-start',
        pointerEvents: 'none',
        transform: 'translateX(-50%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          left: '50%',
          zIndex: 0,
          width: 3,
          height: 34,
          borderRadius: 999,
          background: lineBackground,
          boxShadow: disabled
            ? 'none'
            : active
              ? '0 0 14px rgba(34, 167, 240, 0.34)'
              : '0 0 10px rgba(117, 117, 117, 0.2)',
          opacity: disabled ? 0.72 : 0.96,
          transform: 'translateX(-50%)',
          transformOrigin: 'top center',
          animation: disabled ? 'none' : 'range-center-marker-breathe 1900ms ease-in-out infinite',
          '@keyframes range-center-marker-breathe': {
            '0%, 100%': {
              opacity: active ? 0.82 : 0.68,
              transform: 'translateX(-50%) scaleY(0.94)',
            },
            '50%': {
              opacity: active ? 1 : 0.82,
              transform: 'translateX(-50%) scaleY(1)',
            },
          },
        }}
      />

      <ButtonBase
        component="button"
        type="button"
        disabled={disabled}
        data-testid={`${tid}-range-center-btn`}
        tabIndex={-1}
        sx={{
          position: 'absolute',
          top: 8,
          left: '50%',
          zIndex: 2,
          minWidth: 62,
          height: 24,
          px: 1.25,
          borderRadius: '8px',
          border: '1px solid',
          borderColor,
          backgroundColor,
          color,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
          textTransform: 'lowercase',
          transform: 'translateX(-50%)',
          transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
          '&:not(.Mui-disabled):hover': {
            borderColor: active ? BLUE_DARK : '#9E9E9E',
            backgroundColor: active ? BLUE_HOVER : '#EFEFEF',
            color: active ? BLUE_DARK : MUTED_TEXT,
            boxShadow: active
              ? '0 4px 10px rgba(34, 167, 240, 0.16)'
              : '0 4px 10px rgba(117, 117, 117, 0.12)',
          },
        }}
      >
        {HOOKS.useLocale().t('temp.common.range')}
      </ButtonBase>
    </Box>
  );
};

export const range: FR<HTMLDivElement, Props> = forwardRef((props, ref) => {
  const {
    tid,
    name = 'range',
    label = 'Rango',
    required = false,
    size,
    disabled = false,
    min,
    max,
    step = DEFAULT_STEP,
    formatValue = defaultFormatValue,
    onChange,
  } = props;
  const bounds = resolveBounds(min, max);
  const [isHovering, setIsHovering] = useState(false);
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [showEndpointHints, setShowEndpointHints] = useState(false);
  const rangeTrackRef = useRef<HTMLDivElement | null>(null);
  const preferredActiveThumbRef = useRef<number | null>(null);

  const { verify } = useSiloRealFormContext();
  useEffect(() => verify.uniqueness(name), []);

  const {
    control,
    formState: { errors },
  } = useReactHookFormContext();

  const hasError = Boolean(errors[name]);
  const errorMessage = errors[name]?.message as string | undefined;
  const fieldSx = useFormFieldStyles({ size, base: { sm: 112, md: 168 } }) as RangeFieldSx;

  return (
    <FormControl
      ref={ref}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      sx={{
        ...fieldSx,
        maxWidth: '100%',
        minWidth: `min(100%, ${fieldSx.minWidth})`,
        boxSizing: 'border-box',
        display: 'grid',
        gap: 0,
      }}
      error={hasError}
      required={required}
      disabled={disabled}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={null}
        rules={{ validate: () => true }}
        render={({ field }) => {
          const value = normalizeRangeValue(field.value, bounds);
          const isFullRange = value.min === bounds.min && value.max === bounds.max;
          const lowerAtMin = value.min === bounds.min;
          const upperAtMax = value.max === bounds.max;
          const hasBothEndpointsSelected = !lowerAtMin && !upperAtMax;
          const rangeCenter = getRangeCenterPercent(value, bounds);
          const showLowerBubble = isHovering && !disabled && !lowerAtMin;
          const showUpperBubble = isHovering && !disabled && !upperAtMax;
          const trackBackground = getTrackBackground({ disabled, lowerAtMin, upperAtMax });

          const updateValue = (nextValue: RangeInputValue): void => {
            const nextIsFullRange = nextValue.min === bounds.min && nextValue.max === bounds.max;

            if (!nextIsFullRange) {
              setShowEndpointHints(false);
            }

            field.onChange(toRangeFieldValue(nextValue, bounds));
            onChange?.(nextValue);
          };

          const toggleRange = (shouldShowEndpointHints: boolean): void => {
            if (disabled) return;

            setIsRangeOpen((current) => {
              const nextOpen = !current;

              setShowEndpointHints(nextOpen && shouldShowEndpointHints);
              return nextOpen;
            });
          };

          const resetRange = (): void => {
            updateValue({ ...bounds });
            field.onBlur();
          };

          const resetLowerRange = (): void => {
            updateValue({ min: bounds.min, max: value.max });
            field.onBlur();
          };

          const resetUpperRange = (): void => {
            updateValue({ min: value.min, max: bounds.max });
            field.onBlur();
          };

          const rememberPreferredThumb = (event: ReactPointerEvent<HTMLSpanElement>): void => {
            const eventTarget = event.target as HTMLElement;
            const thumb = eventTarget.closest('.MuiSlider-thumb') as HTMLElement | null;

            if (thumb?.dataset.index === '0' || thumb?.dataset.index === '1') {
              preferredActiveThumbRef.current = Number(thumb.dataset.index);
              return;
            }

            const rangeTrack = rangeTrackRef.current;
            if (!rangeTrack) {
              preferredActiveThumbRef.current = null;
              return;
            }

            const rect = rangeTrack.getBoundingClientRect();
            const pointerPercent = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);

            preferredActiveThumbRef.current = pointerPercent <= rangeCenter ? 0 : 1;
          };

          const updateSliderValue = (nextValue: number[], activeThumb: number): void => {
            const preferredActiveThumb = preferredActiveThumbRef.current ?? activeThumb;
            const nextPoint = clamp(
              nextValue[activeThumb] ?? nextValue[preferredActiveThumb],
              bounds.min,
              bounds.max
            );

            if (preferredActiveThumb === 0) {
              updateValue({ min: Math.min(nextPoint, value.max), max: value.max });
              return;
            }

            updateValue({ min: value.min, max: Math.max(nextPoint, value.min) });
          };

          return (
            <>
              <Box
                data-testid={tid}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid',
                  borderColor: disabled
                    ? DISABLED_BORDER
                    : isRangeOpen || !isFullRange
                      ? GREEN
                      : GREEN_BORDER,
                  borderRadius: isRangeOpen ? '8px 8px 0 0' : '8px',
                  backgroundColor: disabled ? DISABLED_BACKGROUND : isRangeOpen ? GREEN_SOFT : '#FFFFFF',
                  color: disabled ? DISABLED_TEXT : TEXT_COLOR,
                  textAlign: 'left',
                  overflow: 'hidden',
                  transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
                  '&:hover': disabled
                    ? undefined
                    : {
                        borderColor: GREEN,
                        backgroundColor: GREEN_SOFT,
                      },
                }}
              >
                <ButtonBase
                  component="button"
                  type="button"
                  disabled={disabled}
                  data-testid={`${tid}-summary-btn`}
                  aria-label="Mostrar selector de rango"
                  aria-expanded={isRangeOpen}
                  onClick={() => toggleRange(isFullRange)}
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.25,
                    py: 0.9,
                    color: 'inherit',
                    textAlign: 'left',
                    '&.Mui-focusVisible': {
                      boxShadow: `inset 0 0 0 3px rgba(107, 170, 0, 0.22)`,
                    },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <TuneOutlinedIcon
                      sx={{ fontSize: 20, color: disabled ? DISABLED_TEXT : GREEN, flex: '0 0 auto' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: disabled ? DISABLED_TEXT : '#66735B',
                          lineHeight: 1.2,
                        }}
                      >
                        {label}
                        {required && (
                          <Box component="span" sx={{ color: '#EA1818', ml: 0.25 }}>
                            *
                          </Box>
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'block',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 700,
                          color: disabled ? DISABLED_TEXT : !isFullRange ? TEXT_COLOR : '#5C6A50',
                        }}
                      >
                        {getSummary(value, bounds, formatValue)}
                      </Typography>
                    </Box>
                  </Box>

                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      fontSize: 22,
                      color: disabled ? DISABLED_TEXT : GREEN,
                      flex: '0 0 auto',
                      transform: isRangeOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 180ms ease',
                    }}
                  />
                </ButtonBase>

                {!isFullRange && !disabled && (
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Borrar rango"
                    data-testid={`${tid}-clear-btn`}
                    onClick={(event) => {
                      event.stopPropagation();
                      resetRange();
                    }}
                    sx={{
                      alignSelf: 'stretch',
                      flex: '0 0 42px',
                      width: 42,
                      minWidth: 42,
                      height: 'auto',
                      p: 0,
                      borderLeft: '1px solid #F7C2C2',
                      borderRadius: 0,
                      color: '#EA1818',
                      backgroundColor: '#FDE8E8',
                      transition: 'box-shadow 160ms ease',
                      '&:hover': {
                        backgroundColor: '#FDE8E8',
                        boxShadow: '0 0 0 3px rgba(234, 24, 24, 0.12)',
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: isRangeOpen ? '1fr' : '0fr',
                  opacity: isRangeOpen ? 1 : 0,
                  transform: isRangeOpen ? 'translateY(0)' : 'translateY(-4px)',
                  transition: 'grid-template-rows 180ms ease, opacity 180ms ease, transform 180ms ease',
                }}
              >
                <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                  <Box
                    sx={{
                      px: 2,
                      pt: 4.25,
                      pb: 1.25,
                      border: '1px solid',
                      borderTop: 0,
                      borderColor: hasError ? 'error.main' : disabled ? DISABLED_BORDER : '#E2EAD9',
                      borderRadius: '0 0 8px 8px',
                      backgroundColor: disabled ? '#FAFAFA' : '#FFFFFF',
                    }}
                  >
                    <Box ref={rangeTrackRef} sx={{ position: 'relative', minWidth: 0 }}>
                      <RangeCenterMarker
                        tid={tid}
                        left={rangeCenter}
                        active={hasBothEndpointsSelected}
                        disabled={disabled}
                      />

                      <EndpointHint side="start" show={!disabled && showEndpointHints} />
                      <EndpointHint side="end" show={!disabled && showEndpointHints} />

                      <ValueBubble
                        show={showLowerBubble}
                        label={formatValue(value.min)}
                        left={getPositionPercent(value.min, bounds)}
                      />
                      <ValueBubble
                        show={showUpperBubble}
                        label={formatValue(value.max)}
                        left={getPositionPercent(value.max, bounds)}
                      />

                      <Slider
                        data-testid={`${tid}-slider`}
                        disabled={disabled}
                        disableSwap
                        min={bounds.min}
                        max={bounds.max}
                        step={step}
                        value={[value.min, value.max]}
                        valueLabelDisplay="off"
                        onPointerDownCapture={rememberPreferredThumb}
                        onChange={(_, nextValue, activeThumb) => {
                          if (!Array.isArray(nextValue)) return;
                          updateSliderValue(nextValue, activeThumb);
                        }}
                        onChangeCommitted={() => {
                          preferredActiveThumbRef.current = null;
                          field.onBlur();
                        }}
                        sx={{
                          display: 'block',
                          position: 'relative',
                          zIndex: 2,
                          height: 3,
                          px: 0,
                          py: 1.5,
                          color: disabled ? DISABLED_BORDER : isFullRange ? '#D4D4D4' : GREEN,
                          '& .MuiSlider-rail': {
                            height: 3,
                            opacity: 1,
                            borderRadius: 999,
                            background: disabled ? '#D8D8D8' : OUTSIDE_RANGE_LINE,
                          },
                          '& .MuiSlider-track': {
                            height: 3,
                            border: 0,
                            borderRadius: 999,
                            background: trackBackground,
                          },
                          '& .MuiSlider-thumb': {
                            width: 20,
                            height: 20,
                            border: '1.5px solid',
                            backgroundColor: disabled ? DISABLED_BACKGROUND : '#FFFFFF',
                            boxShadow: 'none',
                            transition: 'border-color 160ms ease, box-shadow 160ms ease',
                            '&::before': {
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: 'currentColor',
                              boxShadow: 'none',
                              transform: 'translate(-50%, -50%)',
                            },
                            '&:hover, &.Mui-focusVisible, &.Mui-active': {
                              boxShadow: '0 0 0 5px rgba(107, 170, 0, 0.14)',
                            },
                          },
                          '& .MuiSlider-thumb[data-index="0"]': {
                            borderColor: disabled ? DISABLED_BORDER : lowerAtMin ? EMPTY_BORDER : GREEN,
                            color: disabled ? DISABLED_TEXT : lowerAtMin ? EMPTY_CORE : GREEN,
                          },
                          '& .MuiSlider-thumb[data-index="1"]': {
                            borderColor: disabled ? DISABLED_BORDER : upperAtMax ? EMPTY_BORDER : GREEN,
                            color: disabled ? DISABLED_TEXT : upperAtMax ? EMPTY_CORE : GREEN,
                          },
                          '& .MuiSlider-thumb[data-index="0"]:hover, & .MuiSlider-thumb[data-index="0"].Mui-focusVisible, & .MuiSlider-thumb[data-index="0"].Mui-active':
                            {
                              boxShadow: lowerAtMin
                                ? '0 0 0 5px rgba(117, 117, 117, 0.12)'
                                : '0 0 0 5px rgba(107, 170, 0, 0.14)',
                            },
                          '& .MuiSlider-thumb[data-index="1"]:hover, & .MuiSlider-thumb[data-index="1"].Mui-focusVisible, & .MuiSlider-thumb[data-index="1"].Mui-active':
                            {
                              boxShadow: upperAtMax
                                ? '0 0 0 5px rgba(117, 117, 117, 0.12)'
                                : '0 0 0 5px rgba(107, 170, 0, 0.14)',
                            },
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 0.25,
                      }}
                    >
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 24 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: disabled ? DISABLED_TEXT : MUTED_TEXT, fontSize: 11 }}
                        >
                          {formatValue(bounds.min)}
                        </Typography>

                        {!lowerAtMin && !disabled && (
                          <IconButton
                            type="button"
                            size="small"
                            aria-label="Borrar mínimo del rango"
                            data-testid={`${tid}-clear-min-btn`}
                            onClick={(event) => {
                              event.stopPropagation();
                              resetLowerRange();
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              p: 0,
                              color: 'common.white',
                              backgroundColor: '#EA1818',
                              boxShadow: '0 4px 10px rgba(234, 24, 24, 0.22)',
                              transition: 'box-shadow 160ms ease',
                              '&:hover': {
                                backgroundColor: '#EA1818',
                                boxShadow:
                                  '0 0 0 3px rgba(234, 24, 24, 0.14), 0 6px 14px rgba(234, 24, 24, 0.26)',
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        )}
                      </Box>

                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 24 }}>
                        {!upperAtMax && !disabled && (
                          <IconButton
                            type="button"
                            size="small"
                            aria-label="Borrar máximo del rango"
                            data-testid={`${tid}-clear-max-btn`}
                            onClick={(event) => {
                              event.stopPropagation();
                              resetUpperRange();
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              p: 0,
                              color: 'common.white',
                              backgroundColor: '#EA1818',
                              boxShadow: '0 4px 10px rgba(234, 24, 24, 0.22)',
                              transition: 'box-shadow 160ms ease',
                              '&:hover': {
                                backgroundColor: '#EA1818',
                                boxShadow:
                                  '0 0 0 3px rgba(234, 24, 24, 0.14), 0 6px 14px rgba(234, 24, 24, 0.26)',
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        )}

                        <Typography
                          variant="caption"
                          sx={{ color: disabled ? DISABLED_TEXT : MUTED_TEXT, fontSize: 11 }}
                        >
                          {formatValue(bounds.max)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          );
        }}
      />
      <FormHelperText sx={{ ml: 0, mt: 0.75 }}>{errorMessage}</FormHelperText>
    </FormControl>
  );
});

range.displayName = 'SiloReal-Form-Field-Range';
